import { useState, useEffect } from 'react';
import { useSurvey } from '../../context/SurveyContext';
import { questions, consentQuestions } from '../../config/questions';
import { shouldShowQuestion, isDisqualifyingAnswer } from '../../utils/surveyLogic';
import { getPhoneDigits } from '../../utils/formatters';
import { api } from '../../utils/apiService';
import { trackSurveyStart, trackCompleteRegistration } from '../../utils/analytics';
import Card from '../UI/Card';
import QuestionPage from './QuestionPage';
import ProgressBar from './ProgressBar';
import NavigationButtons from './NavigationButtons';
import TestimonialScreen1 from './InSurveyBoosters/TestimonialScreen1';
import TimelineScreen from './InSurveyBoosters/TimelineScreen';
import TestimonialScreen2 from './InSurveyBoosters/TestimonialScreen2';
import ResultsScreen from './InSurveyBoosters/ResultsScreen';

export default function SurveyContainer({ onComplete }) {
  const {
    product,
    sessionId,
    setSessionId,
    userId,
    setUserId,
    answers,
    currentPage,
    setCurrentPage,
    isLoading,
    setIsLoading,
    setError,
    setCheckoutUrl
  } = useSurvey();

  const [canProceed, setCanProceed] = useState(false);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [showBoosterAfterPage, setShowBoosterAfterPage] = useState(null);

  // Get visible questions based on conditional logic (consent questions are hidden)
  const visibleQuestions = questions.filter(q => shouldShowQuestion(q, answers));
  const totalPages = visibleQuestions.length; // No longer showing consent questions in UI
  const currentQuestion = visibleQuestions[currentPage - 1];

  // Auto-skip if current question is filtered out (undefined)
  useEffect(() => {
    if (!currentQuestion && currentPage <= totalPages && !isDisqualified) {
      // Skip to next available question
      setCurrentPage(prev => prev + 1);
    }
  }, [currentQuestion, currentPage, totalPages, isDisqualified]);

  // Create session on mount
  useEffect(() => {
    if (!sessionId) {
      createSession();
    }
  }, [sessionId]);

  // Track survey start when first page loads
  useEffect(() => {
    if (currentPage === 1) {
      trackSurveyStart();
    }
  }, [currentPage]);

  const createSession = async () => {
    try {
      setIsLoading(true);
      const response = await api.createSession();
      const newSessionId = response.data.session_id;
      setSessionId(newSessionId);
      console.log('✅ Session created:', newSessionId);
    } catch (error) {
      console.error('Failed to create session:', error);
      setError('Failed to start survey. Please refresh and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    // Check for disqualifying answer
    const answer = answers[currentQuestion.id];
    if (currentQuestion.options && isDisqualifyingAnswer(currentQuestion, answer)) {
      setIsDisqualified(true);
      return;
    }

    // Save answer to API if needed
    if (currentQuestion.includeInQA && currentQuestion.apiId && sessionId) {
      await saveAnswer(currentQuestion);
    }

    // Update lead with named fields
    if (currentQuestion.namedField && userId) {
      await updateLead(currentQuestion);
    }

    // Check if survey is complete
    if (currentPage >= totalPages) {
      // Track completion event
      trackCompleteRegistration();
      await completeSession();
      return;
    }

    // Check if we should show a booster after this page
    // Show boosters after pages 5, 6, 7, 8
    if ([5, 6, 7, 8].includes(currentPage)) {
      setShowBoosterAfterPage(currentPage);
      window.scrollTo(0, 0);
      return;
    }

    // Move to next page
    setCurrentPage(currentPage + 1);
    setCanProceed(false);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setCanProceed(true);
    }
  };

  const handleBoosterContinue = () => {
    // Clear the booster display and move to next page
    setShowBoosterAfterPage(null);
    setCurrentPage(currentPage + 1);
    setCanProceed(false);
    window.scrollTo(0, 0);
  };

  const handleBoosterBack = () => {
    // Clear the booster display and return to the current question
    setShowBoosterAfterPage(null);
    setCanProceed(true);
    window.scrollTo(0, 0);
  };

  const saveAnswer = async (question) => {
    try {
      // For textarea fields, send "None" if empty
      let answerValue = answers[question.id];
      if (question.type === 'textarea' && (!answerValue || !answerValue.trim())) {
        answerValue = 'None';
      }

      const answerPayload = {
        [question.apiId]: {
          value: answerValue,
          question: question.text || question.label
        }
      };

      console.log('💾 Saving answer:', {
        questionId: question.apiId,
        sessionId,
        payload: answerPayload
      });

      await api.saveAnswer(sessionId, question.apiId, answerPayload);
      console.log('✅ Answer saved:', question.id);
    } catch (error) {
      console.error('❌ Failed to save answer:', {
        questionId: question.apiId,
        error: error.response?.data || error.message,
        status: error.response?.status,
        fullError: error
      });
    }
  };

  const createLead = async () => {
    try {
      setIsLoading(true);
      const leadData = {
        tenant_id: 15,
        email: answers.email,
        phone: getPhoneDigits(answers.phone),
        name: `${answers.firstName} ${answers.lastName}`,
        first_name: answers.firstName,
        last_name: answers.lastName,
        sessionId: sessionId,
        birthday: answers.dateOfBirth,
        gender: answers.sex,
        lead_state: answers.shippingState,
        zip_code: answers.shippingZip
      };

      console.log('👤 Creating lead with data:', leadData);

      const response = await api.createLead(leadData);
      console.log('✅ Lead creation response:', response);
      console.log('📦 Lead response data:', response.data);

      const newUserId = response.data.user_id;
      setUserId(newUserId);
      console.log('🆔 User ID extracted:', newUserId);
    } catch (error) {
      console.error('❌ Failed to create lead:', {
        error: error.response?.data || error.message,
        status: error.response?.status,
        fullError: error
      });
      setError('Failed to save your information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const updateLead = async (question) => {
    try {
      const updateData = {
        tenant_id: 15,
        [question.namedField]: answers[question.id]
      };
      await api.updateLead(userId, updateData);
      console.log('✅ Lead updated:', question.namedField);
    } catch (error) {
      console.error('Failed to update lead:', error);
    }
  };

  const completeSession = async () => {
    try {
      setIsLoading(true);
      console.log('🔍 Completing session with ID:', sessionId);

      // Create lead if not already created
      if (!userId) {
        console.log('👤 Creating lead before session completion...');
        await createLead();
      }

      // Compile all answers including consents
      const final_answers = {};
      visibleQuestions.forEach(q => {
        if (q.apiId) {
          let answerValue = answers[q.id];
          // For textarea fields, send "None" if empty
          if (q.type === 'textarea' && (!answerValue || !answerValue.trim())) {
            answerValue = 'None';
          }
          if (answerValue) {
            final_answers[q.apiId] = {
              value: answerValue,
              question: q.text || q.label
            };
          }
        }
        // Handle multi-field pages
        if (q.fields) {
          q.fields.forEach(field => {
            if (field.apiId && answers[field.id]) {
              final_answers[field.apiId] = {
                value: answers[field.id],
                question: field.label
              };
            }
          });
        }
      });

      // Auto-include consent answers (agreed via TOS)
      consentQuestions.forEach(q => {
        // Use the first (consent) option value from each consent question
        const consentValue = q.options[0].value;
        final_answers[q.apiId] = {
          value: consentValue,
          question: q.title
        };
      });

      const completionData = {
        final_answers,
        med: [product.medId],
        couponCode: product.couponCode
      };

      const response = await api.completeSession(sessionId, completionData);
      console.log('✅ Complete session response:', response);
      console.log('📦 Response data:', response.data);

      const checkoutUrl = response.data.checkout_url || response.data.checkoutUrl;
      console.log('🔗 Checkout URL extracted:', checkoutUrl);
      setCheckoutUrl(checkoutUrl);

      // Trigger completion callback (shows conversion boosters) - only on success
      onComplete();
    } catch (error) {
      console.error('Failed to complete session:', error);

      // Extract detailed error message from API response
      let errorMessage = 'Failed to complete survey. Please try again.';

      if (error.response) {
        // Check for validation errors (status 400)
        if (error.response.status === 400 && error.response.data) {
          const errorData = error.response.data;

          // Try to extract the validation error message
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.data && errorData.data.message) {
            errorMessage = errorData.data.message;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }

          console.log('📛 Validation error:', errorMessage);
        } else {
          // Other API errors
          errorMessage = `Error ${error.response.status}: ${error.response.statusText || 'Server error'}`;
        }
      }

      setError(errorMessage);

      // DO NOT call onComplete() on error - prevent navigation to boosters
    } finally {
      setIsLoading(false);
    }
  };

  if (isDisqualified) {
    return (
      <Card className="max-w-2xl mx-auto text-center">
        <h2 className="text-2xl font-bold text-charcoal mb-4">
          We're unable to proceed
        </h2>
        <p className="text-lg text-slate mb-6">
          Based on your responses, we are unable to provide this treatment at this time.
          Please consult with your healthcare provider for alternative options.
        </p>
        <p className="text-sm text-slate">
          If you believe this is an error, please contact our support team.
        </p>
      </Card>
    );
  }

  // Don't render if question is undefined (will auto-skip via useEffect)
  if (!currentQuestion) {
    return null;
  }

  // Show in-survey booster screens when triggered
  if (showBoosterAfterPage) {
    // Don't show progress bar during boosters
    switch(showBoosterAfterPage) {
      case 5:
        return <TestimonialScreen1 product={product} onContinue={handleBoosterContinue} onBack={handleBoosterBack} />;
      case 6:
        return <TimelineScreen product={product} onContinue={handleBoosterContinue} onBack={handleBoosterBack} />;
      case 7:
        return <TestimonialScreen2 product={product} onContinue={handleBoosterContinue} onBack={handleBoosterBack} />;
      case 8:
        return <ResultsScreen product={product} onContinue={handleBoosterContinue} onBack={handleBoosterBack} />;
      default:
        return null;
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <ProgressBar current={currentPage} total={totalPages} />

      <Card>
        <QuestionPage
          question={currentQuestion}
          onValidate={setCanProceed}
        />

        <NavigationButtons
          onBack={handleBack}
          onNext={handleNext}
          canGoBack={currentPage > 1}
          canProceed={canProceed}
          isLoading={isLoading}
          nextLabel={currentPage >= totalPages ? 'Complete Survey' : 'Continue'}
        />
      </Card>
    </div>
  );
}
